import React, { useRef, useEffect, useState } from "react";
import Chart from "chart.js";
import BarChartConfiguration from "./barChartConfiguration";

import "./BarChart.css";

export type Props = {
    data: number[];
    labels: string[];
    title?: string;
    yLabel?: string;
    xLabel?: string;
    onImageUpdate?: (image: { url: string; name: string }) => void;
};

const BarChart: React.FC<Props> = props => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const chartRef = useRef<Chart>();
    const [image, setImage] = useState<string>("");

    const getChartInstance = (): Chart | null => {
        if (chartRef.current) {
            return chartRef.current;
        } else {
            if (!canvasRef.current) {
                return null;
            }

            chartRef.current = new Chart(canvasRef.current, BarChartConfiguration(props, setImage));

            Chart.plugins.register({
                beforeDraw: function(chartInstance) {
                    const ctx = chartInstance.ctx;

                    if (ctx == null) {
                        return;
                    }

                    ctx.fillStyle = "white";
                    ctx.fillRect(0, 0, chartInstance.width ?? 0, chartInstance.height ?? 0);
                }
            });

            return chartRef.current;
        }
    };

    useEffect(() => {
        const chart = getChartInstance();

        if (chart != null && chart.data.datasets && chart.options.scales?.yAxes && chart.options.scales?.xAxes) {
            chart.data.datasets[0].data = props.data;
            chart.data.labels = props.labels;
            chart.options.scales.yAxes[0] = {
                scaleLabel: {
                    ...chart.options.scales.yAxes[0].scaleLabel,
                    display: props.xLabel !== undefined,
                    labelString: props.yLabel
                }
            };

            chart.options.scales.xAxes[0] = {
              ticks: {
                ...chart.options.scales.xAxes[0].ticks
              },
                scaleLabel: {
                    ...chart.options.scales.xAxes[0].scaleLabel,
                    display: props.xLabel !== undefined,
                    labelString: props.xLabel
                }
            };

            chart.update();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.data, props.labels, props.title, props.yLabel, props.xLabel]);

    useEffect(() => {
        props.onImageUpdate &&
            props.onImageUpdate({ url: image, name: props.title?.replace(/\s/g, "-") ?? "chart.png" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [image]);

    return (
        <div className="BarChart">
            <div className="BarChart-header">
                <span className="BarChart-title">{props.title}</span>
            </div>

            {props.data.length === 0 ? (
                <div className="BarChart-centerOverlay">No data found for current filters.</div>
            ) : null}
            
            <canvas ref={canvasRef} style={{ visibility: props.data.length === 0 ? "hidden" : "visible" }} />
        </div>
    );
};

export default BarChart;
